import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	AlertDescription,
	AlertTitle,
	Badge,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Heading,
	Spinner,
	Text,
} from "@cognigy-internal/ui-component-library";
import { type PublicConfig, type SignupApi, createSignupApi } from "lib/api";
import { loadAnalytics } from "lib/scripts";
import heroBg from "../assets/hero-bg.png";
import niceCognigyLogo from "../assets/nice-cognigy-logo.svg";
import { Benefits, DemoCard, Footer } from "./Sections";
import { SignupForm, type SignupFormProps } from "./SignupForm";

type View = { kind: "form" } | { kind: "redirecting" } | { kind: "failed"; message: string };

export interface AppProps {
	api?: SignupApi;
	navigate?: (url: string) => void;
	loadCaptcha?: SignupFormProps["loadCaptcha"];
	analytics?: typeof loadAnalytics;
}

export function App({
	api: apiProp,
	navigate = url => window.location.assign(url),
	loadCaptcha,
	analytics = loadAnalytics,
}: AppProps) {
	const api = useMemo(() => apiProp ?? createSignupApi(), [apiProp]);
	const [config, setConfig] = useState<PublicConfig>();
	const [configError, setConfigError] = useState(false);
	const [view, setView] = useState<View>({ kind: "form" });

	useEffect(() => {
		api
			.getConfig()
			.then(cfg => {
				setConfig(cfg);
				analytics(cfg.gtmId, cfg.hubspotPortalId);
			})
			.catch(() => setConfigError(true));
	}, [api, analytics]);

	const loginUrl = config?.cognigyUiUrl ? `${config.cognigyUiUrl}/login` : undefined;

	return (
		<div className="flex min-h-dvh flex-col">
			<div className="relative isolate overflow-hidden">
				<img
					src={heroBg}
					alt=""
					className="pointer-events-none absolute inset-0 -z-20 size-full object-cover"
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 -z-10 bg-white opacity-80 mix-blend-soft-light"
				/>
				<header className="flex items-center justify-between gap-4 px-6 py-3">
					<a
						href="https://www.cognigy.com"
						className="focus-visible:ring-primary-300 rounded-sm outline-none focus-visible:ring-2"
					>
						<img
							src={niceCognigyLogo}
							alt="NiCE Cognigy"
							width={147}
							height={24}
						/>
					</a>
					{loginUrl && (
						<Text
							variant="body-sm"
							className="text-primary-900 text-right text-xs"
						>
							<span className="hidden sm:inline">Already have a trial account? </span>
							<a
								href={loginUrl}
								className="text-primary-500 underline-offset-4 hover:underline"
							>
								Log in
							</a>
						</Text>
					)}
				</header>

				<main className="mx-auto grid w-full max-w-[72.375rem] grid-cols-1 gap-10 px-6 py-10 md:px-8 lg:grid-cols-[minmax(0,25rem)_minmax(0,34.375rem)] lg:justify-between lg:gap-16 lg:py-8 lg:pb-16">
					<div className="lg:pt-6">
						<Badge className="rounded-md px-2 py-0.5">Free trial</Badge>
						<Heading
							as="h1"
							variant="display"
							className="mt-4 text-4xl leading-tight font-bold tracking-tight lg:text-5xl lg:leading-[56px]"
						>
							Build your first CX AI agents in minutes
						</Heading>
						<Text
							variant="body"
							className="mt-4 text-sm leading-5"
						>
							You’re just one step away. Create your account and see how AI-first CX works in
							practice.
						</Text>
						<Benefits />
					</div>

					<Card className="gap-4 rounded-xl p-2 shadow-xs sm:p-4">
						<CardHeader className="gap-1.5">
							<CardTitle>
								<Heading
									as="h2"
									variant="h3"
									className="text-lg leading-7 font-medium"
								>
									{view.kind === "failed"
										? "Something went wrong"
										: "Explore the NiCE Cognigy platform"}
								</Heading>
							</CardTitle>
							{view.kind !== "failed" && (
								<CardDescription>Create your free trial account</CardDescription>
							)}
						</CardHeader>
						<CardContent>
							{configError && (
								<Alert
									variant="destructive"
									announce="assertive"
								>
									<AlertTitle>Signup is currently unavailable</AlertTitle>
									<AlertDescription>
										Please reload the page or try again in a few minutes.
									</AlertDescription>
								</Alert>
							)}
							{!config && !configError && (
								<div className="flex justify-center py-16">
									<Spinner label="Loading the signup form" />
								</div>
							)}
							{config && view.kind === "form" && (
								<SignupForm
									api={api}
									config={config}
									onCreated={url => {
										setView({ kind: "redirecting" });
										navigate(url);
									}}
									onFailed={message => setView({ kind: "failed", message })}
									{...(loadCaptcha && { loadCaptcha })}
								/>
							)}
							{view.kind === "redirecting" && (
								<div
									className="flex flex-col items-center gap-4 py-12 text-center"
									role="status"
								>
									<Spinner size="lg" />
									<Text variant="body">Your trial is ready. Taking you to NiCE Cognigy…</Text>
								</div>
							)}
							{view.kind === "failed" && (
								<FailedSignup
									message={view.message}
									loginUrl={loginUrl}
								/>
							)}
						</CardContent>
					</Card>
				</main>
			</div>

			<DemoCard />
			<div className="flex-1" />
			<Footer />
		</div>
	);
}

function FailedSignup({ message, loginUrl }: { message: string; loginUrl: string | undefined }) {
	return (
		<div className="flex flex-col gap-4">
			<Alert
				variant="destructive"
				announce="assertive"
				focusOnMount
			>
				<AlertTitle>We could not create your trial</AlertTitle>
				<AlertDescription>{message}</AlertDescription>
			</Alert>
			<Text variant="body">
				<strong>Already signed up?</strong> Please log in
				{loginUrl ? (
					<>
						{" "}
						at{" "}
						<a
							href={loginUrl}
							className="text-primary font-semibold underline underline-offset-2"
						>
							{loginUrl.replace(/^https?:\/\//, "").replace(/\/login$/, "")}
						</a>
					</>
				) : null}
				. If you don't remember your password, use the "Forgot password" function.
			</Text>
			<Text
				variant="body"
				className="text-muted-foreground"
			>
				Our team has been notified and will get in touch with you shortly.
			</Text>
		</div>
	);
}
